'use client';

import {
  faChevronDown,
  faSearch,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Menu as MenuIcon } from '@mui/icons-material';
import {
  AppBar,
  Box,
  Button,
  Container,
  IconButton,
  InputBase,
  Link,
  Menu,
  MenuItem,
  Paper,
  Toolbar,
} from '@mui/material';
import Image from 'next/image';
import React, { useEffect, useRef, useState } from 'react';

export interface NavigationItem {
  label: string;
  href: string;
  active?: boolean;
  hasDropdown?: boolean;
  dropdownItems?: { label: string; href: string }[];
}

export interface HeaderProps {
  logo?: string;
  navigationItems: NavigationItem[];
  isActive?: boolean;
}

const Header = ({ logo, navigationItems, isActive = true }: HeaderProps) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  // Scroll event handler
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Show header when scrolling up or at the top
      if (currentScrollY < lastScrollY || currentScrollY < 100) {
        setIsVisible(true);
      } else {
        // Hide header when scrolling down (but not at the very top)
        setIsVisible(false);
      }

      setLastScrollY(currentScrollY);
    };

    // Throttle scroll events for better performance
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleScroll);
    return () => window.removeEventListener('scroll', throttledHandleScroll);
  }, [lastScrollY]);

  // Click outside handler to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setActiveDropdown(null);
      }
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
  };

  const toggleDropdown = (label: string) => {
    setActiveDropdown(activeDropdown === label ? null : label);
  };

  const closeDropdown = () => {
    setActiveDropdown(null);
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchorEl(null);
  };

  const logoUrl = logo || '/logo.png';
  const logoAlt = 'Bách Việt Logo';

  return (
    <AppBar
      id='header-app-bar'
      position='fixed'
      elevation={0}
      sx={{
        justifyContent: 'center',
        backgroundColor: 'white',
        color: 'primary.main',
        boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.08)',
        height: { xs: '70px', md: '90px' }, // Responsive height
        transform: isVisible ? 'translateY(0)' : 'translateY(-100%)',
        transition: 'transform 0.3s ease-in-out',
        zIndex: 1000,
      }}
    >
      <Container id='header-container' maxWidth='xl' disableGutters>
        <Toolbar
          id='header-toolbar'
          sx={{
            height: '100%',
            px: { xs: 1, sm: 2, lg: 4 }, // Responsive padding
            minHeight: 'auto', // Override MUI default
            position: 'relative',
          }}
        >
          {/* Logo */}
          <Box
            id='header-logo-container'
            sx={{
              position: 'absolute',
              left: { xs: 16, sm: 32, lg: 64 }, // Responsive left positioning
              display: 'flex',
              alignItems: 'center',
              zIndex: 1,
            }}
          >
            <Link
              id='header-logo-link'
              href='/'
              sx={{
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                '&:hover': {
                  opacity: 0.8,
                },
                transition: 'opacity 0.2s',
              }}
            >
              <Box
                id='header-logo-box'
                sx={{
                  height: { xs: '24px', md: '32px' },
                  maxWidth: { xs: '200px', md: '302px' },
                  position: 'relative',
                }}
              >
                <Image
                  id='header-logo-image'
                  src={logoUrl}
                  alt={logoAlt}
                  width={302}
                  height={30}
                  priority={true}
                  style={{
                    height: '100%',
                    width: '100%',
                    objectFit: 'contain',
                  }}
                />
              </Box>
            </Link>
          </Box>

          {/* Desktop Navigation - Hidden on mobile */}
          <Box
            id='header-desktop-navigation'
            sx={{
              display: { xs: 'none', md: 'none', lg: 'flex' },
              gap: '60px',
              position: 'absolute',
              left: '55%',
              transform: 'translateX(-50%)',
              alignItems: 'center',
              zIndex: 1,
            }}
            ref={dropdownRef}
          >
            {navigationItems?.map((item, index) => (
              <Box
                key={item.label}
                id={`header-nav-item-${index}`}
                sx={{ position: 'relative' }}
              >
                {item.hasDropdown ? (
                  <Box
                    id={`header-dropdown-container-${index}`}
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <Button
                      id={`header-nav-button-${index}`}
                      href={item.href}
                      sx={{
                        color: 'primary.main',
                        textTransform: 'none',
                        fontSize: '18px',
                        fontWeight: 600,
                        fontFamily: 'var(--font-inter), Inter, sans-serif',
                        lineHeight: '1.2102272245619032em',
                        whiteSpace: 'nowrap',
                        minWidth: 'auto',
                        padding: '0',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: 'grey.700',
                        },
                      }}
                      onClick={() => toggleDropdown(item.label)}
                    >
                      {item.label}
                    </Button>
                    <IconButton
                      id={`header-dropdown-icon-${index}`}
                      size='small'
                      onClick={() => toggleDropdown(item.label)}
                      aria-label={`Toggle ${item.label} dropdown menu`}
                      sx={{
                        padding: '4px',
                        marginLeft: '4px',
                        transform:
                          activeDropdown === item.label
                            ? 'rotate(180deg)'
                            : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        style={{
                          width: '12px',
                          height: '12px',
                          color: 'inherit',
                        }}
                      />
                    </IconButton>
                    {activeDropdown === item.label && item.dropdownItems && (
                      <Box
                        id={`header-dropdown-menu-${index}`}
                        sx={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          marginTop: '8px',
                          width: '192px',
                          backgroundColor: 'white',
                          borderRadius: '6px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                          border: '1px solid #e5e7eb',
                          zIndex: 50,
                        }}
                      >
                        {item.dropdownItems.map(
                          (dropdownItem, dropdownIndex) => (
                            <MenuItem
                              key={dropdownItem.label}
                              id={`header-dropdown-item-${index}-${dropdownIndex}`}
                              component='a'
                              href={dropdownItem.href}
                              onClick={closeDropdown}
                              sx={{
                                fontSize: '14px',
                                color: 'grey.700',
                                '&:hover': {
                                  backgroundColor: 'grey.100',
                                },
                              }}
                            >
                              {dropdownItem.label}
                            </MenuItem>
                          )
                        )}
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Button
                    id={`header-nav-button-${index}`}
                    href={item.href}
                    sx={{
                      color: 'primary.main',
                      textTransform: 'none',
                      fontSize: '18px',
                      fontWeight: 600,
                      fontFamily: 'var(--font-inter), Inter, sans-serif',
                      lineHeight: '1.2102272245619032em',
                      whiteSpace: 'nowrap',
                      minWidth: 'auto',
                      padding: '0',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        color: 'grey.700',
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                )}
              </Box>
            ))}
          </Box>

          {/* Search and Mobile Menu */}
          <Box
            id='header-actions-container'
            sx={{
              position: 'absolute',
              right: { xs: 16, sm: 32, lg: 64 }, // Responsive right positioning
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.5, sm: 1 }, // Responsive gap
              zIndex: 1,
            }}
            ref={searchRef}
          >
            {/* Search */}
            {isActive && (
              <Box id='header-search-container' sx={{ position: 'relative' }}>
                {isSearchOpen ? (
                  <Paper
                    id='header-search-paper'
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px 12px',
                      width: { xs: '180px', sm: '220px', md: '256px' }, // Responsive width
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      borderRadius: '6px',
                    }}
                  >
                    <InputBase
                      id='header-search-input'
                      placeholder='Tìm kiếm...'
                      autoFocus
                      onBlur={() => setIsSearchOpen(false)}
                      sx={{
                        flex: 1,
                        fontSize: '14px',
                        fontFamily: 'var(--font-inter), Inter, sans-serif',
                      }}
                    />
                    <IconButton
                      id='header-search-close-button'
                      size='small'
                      onClick={toggleSearch}
                      aria-label='Close search'
                      sx={{
                        padding: '4px',
                        color: 'grey.500',
                        '&:hover': {
                          color: 'grey.700',
                        },
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faTimes}
                        style={{
                          width: '14px',
                          height: '14px',
                        }}
                      />
                    </IconButton>
                  </Paper>
                ) : (
                  <IconButton
                    id='header-search-toggle-button'
                    onClick={toggleSearch}
                    aria-label='Open search'
                    sx={{
                      color: 'grey.500',
                      '&:hover': {
                        color: 'grey.700',
                      },
                      width: { xs: '36px', sm: '40px' }, // Responsive touch target
                      height: { xs: '36px', sm: '40px' },
                      padding: '8px',
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faSearch}
                      style={{
                        width: '16px',
                        height: '16px',
                      }}
                    />
                  </IconButton>
                )}
              </Box>
            )}

            {/* Mobile Menu Button */}
            <IconButton
              id='header-mobile-menu-button'
              onClick={handleMobileMenuOpen}
              aria-label='Open mobile menu'
              sx={{
                display: { xs: 'flex', md: 'flex', lg: 'none' },
                color: 'grey.500',
                '&:hover': {
                  color: 'grey.700',
                },
                width: { xs: '36px', sm: '40px' }, // Responsive touch target
                height: { xs: '36px', sm: '40px' },
                padding: '8px',
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </Container>

      {/* Mobile Menu */}
      <Menu
        id='header-mobile-menu'
        anchorEl={mobileMenuAnchorEl}
        open={Boolean(mobileMenuAnchorEl)}
        onClose={handleMobileMenuClose}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: '100%',
            marginTop: '8px',
            backgroundColor: 'white',
            borderBottom: '1px solid #e5e7eb',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            borderRadius: '0 0 8px 8px',
          },
        }}
      >
        {navigationItems?.map((item, index) => (
          <Box key={item.label} id={`header-mobile-nav-item-${index}`}>
            <MenuItem
              id={`header-mobile-menu-item-${index}`}
              component='a'
              href={item.href}
              onClick={handleMobileMenuClose}
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: 'primary.main',
                fontFamily: 'var(--font-inter), Inter, sans-serif',
                lineHeight: '1.2102272245619032em',
                padding: '16px',
                borderBottom: '1px solid #f3f4f6',
                '&:hover': {
                  backgroundColor: 'grey.100',
                  color: 'grey.700',
                },
                '&:last-child': {
                  borderBottom: 'none',
                },
              }}
            >
              {item.label}
            </MenuItem>
            {item.hasDropdown && item.dropdownItems && (
              <Box
                id={`header-mobile-dropdown-container-${index}`}
                sx={{
                  marginLeft: '16px',
                  backgroundColor: '#f9fafb',
                }}
              >
                {item.dropdownItems.map((dropdownItem, dropdownIndex) => (
                  <MenuItem
                    key={dropdownItem.label}
                    id={`header-mobile-dropdown-item-${index}-${dropdownIndex}`}
                    component='a'
                    href={dropdownItem.href}
                    onClick={handleMobileMenuClose}
                    sx={{
                      fontSize: '14px',
                      color: 'grey.600',
                      padding: '12px 16px',
                      fontFamily: 'var(--font-inter), Inter, sans-serif',
                      '&:hover': {
                        backgroundColor: 'grey.200',
                        color: 'grey.800',
                      },
                    }}
                  >
                    {dropdownItem.label}
                  </MenuItem>
                ))}
              </Box>
            )}
          </Box>
        ))}
      </Menu>
    </AppBar>
  );
};

export default Header;
